import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getElectricalCriteriaNote,
  getClickPoint,
  getElectricalClickPoints,
  getElectricalMeasuredBy,
  getElectricalMeasurementConditions,
  getElectricalOperatingVoltage,
  getElectricalPhotoSrc,
  getElectricalSignatureText,
  getElectricalTemperatureDiff,
  getElectricalTitleMain,
  getElectricalTitleMeta,
  getInspectorDateParts,
  toText,
} from "components/project/electricalSafetyReportUtils";
import {
  getUserSignatureProfile,
  SIGNATURE_IMAGE_MAX_SIZE,
  SIGNATURE_STAGE_SIZE,
} from "lib/utils/userSignatureProfile";
import ElectricalSafetyEditModal from "./components/ElectricalSafetyEditModal";

const CM_TO_PX = 96 / 2.54;
const TARGET_TABLE_WIDTH_PX = 1305;

const COLUMN_SIZES_CM = [
  ["A", 3.12],
  ["B", 1.27],
  ["C", 1.27],
  ["D", 0.64],
  ["E", 1.67],
  ["F", 0.98],
  ["G", 0.58],
  ["H", 2.12],
  ["I", 0.58],
  ["J", 0.58],
  ["K", 2.33],
  ["L", 0.56],
  ["M", 0.48],
  ["N", 2.8],
];

const ROW_SIZES_CM = [
  1.06, 1.06, 1.4, 0.64, 0.77, 0.98, 1.53, 0.82, 0.87, 1.14, 0.79, 0.82, 1.51,
  4.71, 1.03, 1.53, 3.12,
];

const TOTAL_COLUMN_WIDTH_CM = COLUMN_SIZES_CM.reduce(
  (sum, [, cm]) => sum + cm,
  0,
);
const TABLE_SCALE = Number(
  (TARGET_TABLE_WIDTH_PX / (TOTAL_COLUMN_WIDTH_CM * CM_TO_PX)).toFixed(5),
);

const columns = distributeColumnPixels(
  COLUMN_SIZES_CM.map(([label, cm]) => ({
    label,
    rawPx: toRawPx(cm),
  })),
  TARGET_TABLE_WIDTH_PX,
);

const rows = ROW_SIZES_CM.map((cm, index) => ({
  label: index + 1,
  px: Math.max(1, Math.round(toRawPx(cm))),
}));

const totalWidthPx = TARGET_TABLE_WIDTH_PX;

function getColumnRangeWidthPx(startColumn, endColumn) {
  return columns
    .slice(startColumn - 1, endColumn)
    .reduce((sum, column) => sum + column.px, 0);
}

const INSPECTOR_SECTION_WIDTHS_PX = Object.freeze({
  measuredBy: getColumnRangeWidthPx(1, 3),
  signature: getColumnRangeWidthPx(4, 6),
  date: getColumnRangeWidthPx(7, 14),
});

const INSPECTOR_SECTION_WIDTHS_PERCENT = Object.freeze({
  measuredBy: Number(
    ((INSPECTOR_SECTION_WIDTHS_PX.measuredBy / totalWidthPx) * 100).toFixed(4),
  ),
  signature: Number(
    ((INSPECTOR_SECTION_WIDTHS_PX.signature / totalWidthPx) * 100).toFixed(4),
  ),
  date: Number(
    ((INSPECTOR_SECTION_WIDTHS_PX.date / totalWidthPx) * 100).toFixed(4),
  ),
});

// HELPER: cm 단위로 정의된 템플릿 치수를 현재 보고서 픽셀 기준으로 변환한다.
function distributeColumnPixels(rawColumns, targetWidthPx) {
  const columnsWithPixels = rawColumns.map((column) => ({
    ...column,
    px: Math.floor(column.rawPx),
  }));
  let remainingPixels =
    targetWidthPx -
    columnsWithPixels.reduce((sum, column) => sum + column.px, 0);
  const byRemainder = columnsWithPixels
    .map((column, index) => ({
      index,
      remainder: column.rawPx - Math.floor(column.rawPx),
    }))
    .sort((a, b) => b.remainder - a.remainder);

  for (let i = 0; i < byRemainder.length && remainingPixels > 0; i += 1) {
    columnsWithPixels[byRemainder[i].index].px += 1;
    remainingPixels -= 1;
  }

  return columnsWithPixels.map(({ label, px }) => ({ label, px }));
}

function toRawPx(cm) {
  return cm * CM_TO_PX * TABLE_SCALE;
}

// HELPER: 빈 값은 공백 placeholder로 치환해 셀 높이와 레이아웃을 안정적으로 유지한다.
function getCellValue(value, placeholder = "\u00A0") {
  if (value == null) return placeholder;
  const text = String(value).trim();
  return text || placeholder;
}

// HELPER: 진단 이미지가 없을 때도 같은 자리에서 placeholder UI를 렌더링한다.
function ImagePlaceholder({
  src,
  alt,
  label,
  actionHint = "",
  onClick = null,
}) {
  if (src) {
    return <img className="electricalSafetyReport image" src={src} alt={alt} />;
  }

  if (onClick) {
    return (
      <button
        type="button"
        className="electricalSafetyReport imagePlaceholder isAction"
        onClick={onClick}
      >
        <span
          className="electricalSafetyReport actionIcon"
          aria-hidden="true"
        />
        <span className="electricalSafetyReport actionTitle">{label}</span>
        {actionHint ? (
          <small className="electricalSafetyReport actionHint">
            {actionHint}
          </small>
        ) : null}
      </button>
    );
  }

  return (
    <div className="electricalSafetyReport imagePlaceholder">
      <span>{label}</span>
    </div>
  );
}

/*
 * ElectricalSafetyReport 역할:
 * - 전기안전 진단 보고서를 고정 그리드 테이블 형식으로 렌더링한다.
 * - 파일 데이터와 파생 계산값을 조합해 제목, 측정 조건, 온도차, 진단 이미지를 표시한다.
 * - 편집 모드에서는 보고서 본문은 그대로 유지하고 편집 모달만 추가로 제공한다.
 *
 * 처리 흐름:
 * 1. 보고서 본문에 필요한 제목, 측정값, 이미지 경로, 날짜 조각을 계산한다.
 * 2. 편집 모드가 열리면 ESC 닫기와 body 클래스 토글을 연결한다.
 * 3. 고정 폭 테이블에 전기안전 보고서 각 섹션을 읽기 전용으로 렌더링한다.
 * 4. 편집 모드일 때만 수정용 모달을 추가로 렌더링해 저장/취소 액션을 연결한다.
 */
export default function ElectricalSafetyReport({
  file = {},
  images = {},
  user = null,
  onRequireTripleSpot = () => {},
  maxViewportHeight = null,
  disableResponsiveScale = false,
  previewScale = 1,
  isReadOnly = false,
  handleChange = () => {},
  onSave = () => {},
  onCancel = () => {},
  isSaving = false,
  hasChanges = false,
}) {
  const { t } = useTranslation();
  const gridWrapRef = useRef(null);
  const gridStageRef = useRef(null);
  const [responsiveLayout, setResponsiveLayout] = useState({
    scale: 1,
    height: null,
  });
  const numericPreviewScale = Number(previewScale);
  const previewScaleMultiplier =
    Number.isFinite(numericPreviewScale) && numericPreviewScale > 0
      ? numericPreviewScale
      : 1;
  // STEP 1: 보고서 본문에 사용할 파생 텍스트, 측정 포인트, 이미지 소스를 계산한다.
  const clickPoints = getElectricalClickPoints(file);
  const titleMain = getElectricalTitleMain(file);
  const titleMeta = getElectricalTitleMeta(file);
  const criteriaNote = getElectricalCriteriaNote(file);
  const measuredBy = getElectricalMeasuredBy(file, user);
  const signatureText = getElectricalSignatureText(file);
  const operatingVoltage = getElectricalOperatingVoltage(file);
  const measurementConditions = getElectricalMeasurementConditions(file);
  const temperatureDiff = getElectricalTemperatureDiff(file);
  const photoSrc = getElectricalPhotoSrc(images);
  const thermalSrc = toText(images?.markup_image_triplespot);
  const signatureProfile = getUserSignatureProfile(user);
  const point1 = getClickPoint(clickPoints[0], "Point 1");
  const point2 = getClickPoint(clickPoints[1], "Point 2");
  const point3 = getClickPoint(clickPoints[2], "Point 3");
  const inspectorDate = getInspectorDateParts(file);
  const isEditMode = isReadOnly;
  const inspectorBlockStyles = {
    measuredBy: {
      flex: `0 0 ${INSPECTOR_SECTION_WIDTHS_PERCENT.measuredBy}%`,
      maxWidth: `${INSPECTOR_SECTION_WIDTHS_PERCENT.measuredBy}%`,
    },
    signature: {
      flex: `0 0 ${INSPECTOR_SECTION_WIDTHS_PERCENT.signature}%`,
      maxWidth: `${INSPECTOR_SECTION_WIDTHS_PERCENT.signature}%`,
      justifyContent: "flex-start",
      textAlign: "left",
    },
    date: {
      flex: `0 0 ${INSPECTOR_SECTION_WIDTHS_PERCENT.date}%`,
      maxWidth: `${INSPECTOR_SECTION_WIDTHS_PERCENT.date}%`,
    },
  };

  // STEP 2: 편집 모달이 열려 있는 동안 ESC 닫기와 body 상태 클래스를 동기화한다.
  useEffect(() => {
    if (!isEditMode) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onCancel();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    document.body.classList.add("reportEditModalOpen");

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("reportEditModalOpen");
    };
  }, [isEditMode, onCancel]);

  useLayoutEffect(() => {
    const wrapElement = gridWrapRef.current;
    const stageElement = gridStageRef.current;

    if (!wrapElement || !stageElement) return undefined;

    if (disableResponsiveScale) {
      setResponsiveLayout({
        scale: 1,
        height: null,
      });
      return undefined;
    }

    let frameId = 0;

    const measure = () => {
      const widthScale = wrapElement.clientWidth / totalWidthPx;
      const heightScale =
        maxViewportHeight && stageElement.offsetHeight > 0
          ? maxViewportHeight / stageElement.offsetHeight
          : 1;
      const nextScale =
        Math.min(1, widthScale, heightScale) * previewScaleMultiplier;
      const nextHeight = Number(
        (stageElement.offsetHeight * nextScale).toFixed(2),
      );

      setResponsiveLayout((prev) => {
        const prevHeight = prev.height ?? 0;

        if (
          Math.abs(prev.scale - nextScale) < 0.001 &&
          Math.abs(prevHeight - nextHeight) < 0.5
        ) {
          return prev;
        }

        return {
          scale: nextScale,
          height: nextHeight,
        };
      });
    };

    const scheduleMeasure = () => {
      cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(measure);
    };

    scheduleMeasure();

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            scheduleMeasure();
          });

    resizeObserver?.observe(wrapElement);
    resizeObserver?.observe(stageElement);
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [disableResponsiveScale, maxViewportHeight, previewScaleMultiplier]);

  // STEP 3: 전기안전 보고서 본문과 편집 모달을 현재 모드에 맞춰 함께 렌더링한다.
  return (
    <>
      <div
        ref={gridWrapRef}
        className={`electricalSafetyReport gridWrap${
          disableResponsiveScale ? " is-fullSize" : ""
        }`}
        style={{
          "--electrical-safety-scale": responsiveLayout.scale,
          "--electrical-safety-scaled-height":
            responsiveLayout.height == null
              ? "auto"
              : `${responsiveLayout.height}px`,
        }}
      >
        <div className="electricalSafetyReport scaledViewport">
          <div
            ref={gridStageRef}
            className="electricalSafetyReport scaledStage"
          >
            <table
              className="electricalSafetyReport table gridTable"
              style={{ width: `${totalWidthPx}px` }}
            >
              <colgroup>
                {columns.map((column) => (
                  <col
                    key={column.label}
                    style={{
                      width: `${column.px}px`,
                      minWidth: `${column.px}px`,
                    }}
                  />
                ))}
              </colgroup>

              <tbody>
                <tr style={{ height: `${rows[0].px}px` }}>
                  <td
                    colSpan={14}
                    className="electricalSafetyReport blankCell noBorderCell"
                  />
                </tr>

                <tr style={{ height: `${rows[1].px}px` }}>
                  <th
                    colSpan={14}
                    className="electricalSafetyReport title noBorderCell"
                  >
                    <span className="electricalSafetyReport titleMain">
                      {titleMain}
                    </span>
                    <span className="electricalSafetyReport titleMeta">
                      {titleMeta}
                    </span>
                  </th>
                </tr>

                <tr style={{ height: `${rows[2].px}px` }}>
                  {columns.map((column) => (
                    <td
                      key={`blank-${column.label}`}
                      className="electricalSafetyReport blankCell noBorderCell"
                    />
                  ))}
                </tr>

                <tr style={{ height: `${rows[3].px}px` }}>
                  <td
                    colSpan={14}
                    className="electricalSafetyReport inspector noTopBorderCell"
                  >
                    <div className="electricalSafetyReport inspectorLine">
                      <span
                        className="electricalSafetyReport inspectorBlock inspectorBlockStart"
                        style={inspectorBlockStyles.measuredBy}
                      >
                        측정자 : {measuredBy ?? ""}
                      </span>
                      <span
                        className="electricalSafetyReport inspectorBlock inspectorBlockCenter"
                        style={inspectorBlockStyles.signature}
                      >
                        <span
                          className="electricalSafetyReport inspectorSignatureStage"
                          style={{
                            width: `${SIGNATURE_STAGE_SIZE.width}px`,
                            // height: `${SIGNATURE_STAGE_SIZE.height}px`,
                            height: `45px`,
                          }}
                        >
                          <span className="electricalSafetyReport inspectorSignature">
                            {getCellValue(signatureText)}
                          </span>

                          {signatureProfile?.imageUrl ? (
                            <img
                              className="electricalSafetyReport inspectorSignatureImage"
                              src={signatureProfile.imageUrl}
                              alt={`${measuredBy || "Inspector"} signature`}
                              style={{
                                maxWidth: `${SIGNATURE_IMAGE_MAX_SIZE.maxWidth}px`,
                                maxHeight: "45px",
                              }}
                            />
                          ) : null}
                        </span>
                      </span>
                      <span
                        className="electricalSafetyReport inspectorBlock inspectorBlockEnd"
                        style={inspectorBlockStyles.date}
                      >
                        <span className="electricalSafetyReport inspectorDate">
                          <span>{inspectorDate.year}년</span>
                          <span>{inspectorDate.month}월</span>
                          <span>{inspectorDate.day}일</span>
                        </span>
                      </span>
                    </div>
                  </td>
                </tr>

                <tr style={{ height: `${rows[4].px}px` }}>
                  <th className="electricalSafetyReport label">측정대상</th>
                  <td
                    colSpan={3}
                    className="electricalSafetyReport value centeredValueCell"
                  >
                    {getCellValue(file?.facility_name)}
                  </td>
                  <th colSpan={3} className="electricalSafetyReport label">
                    사용전압
                  </th>
                  <td
                    colSpan={3}
                    className="electricalSafetyReport value centeredValueCell"
                  >
                    {getCellValue(operatingVoltage)}
                  </td>
                  <th colSpan={3} className="electricalSafetyReport label">
                    측정조건
                  </th>
                  <td className="electricalSafetyReport value centeredValueCell">
                    {getCellValue(measurementConditions)}
                  </td>
                </tr>

                <tr style={{ height: `${rows[5].px}px` }}>
                  <th
                    colSpan={14}
                    className="electricalSafetyReport sectionTitle noFillCell"
                  >
                    1. 판정기준(3상 비교법)
                  </th>
                </tr>

                <tr style={{ height: `${rows[6].px}px` }}>
                  <th
                    colSpan={2}
                    className="electricalSafetyReport subhead diagonalHeader"
                  >
                    <svg
                      className="electricalSafetyReport diagonalHeaderLine"
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                      aria-hidden="true"
                    >
                      <line x1="0" y1="0" x2="100" y2="100" />
                    </svg>
                    <span className="electricalSafetyReport diagonalHeaderTop">
                      구분
                    </span>
                    <span className="electricalSafetyReport diagonalHeaderBottom">
                      판정요소
                    </span>
                  </th>
                  <th colSpan={3} className="electricalSafetyReport subhead">
                    정상
                  </th>
                  <th colSpan={3} className="electricalSafetyReport subhead">
                    요주의
                  </th>
                  <th colSpan={3} className="electricalSafetyReport subhead">
                    이상
                  </th>
                  <th colSpan={3} className="electricalSafetyReport subhead">
                    비고
                  </th>
                </tr>

                <tr style={{ height: `${rows[7].px}px` }}>
                  <th
                    colSpan={2}
                    className="electricalSafetyReport label noBoldCell"
                  >
                    온도차
                  </th>
                  <td
                    colSpan={3}
                    className="electricalSafetyReport center noBoldCell"
                  >
                    5℃ 이하
                  </td>
                  <td
                    colSpan={3}
                    className="electricalSafetyReport center noBoldCell"
                  >
                    5℃ 초과 ~ 10℃
                  </td>
                  <td
                    colSpan={3}
                    className="electricalSafetyReport center noBoldCell"
                  >
                    10℃ 이상
                  </td>
                  <td
                    colSpan={3}
                    className="electricalSafetyReport blankCell"
                  />
                </tr>

                <tr style={{ height: `${rows[8].px}px` }}>
                  <td
                    colSpan={14}
                    className="electricalSafetyReport note noBottomBorderCell"
                  >
                    ※{criteriaNote}
                  </td>
                </tr>

                <tr style={{ height: `${rows[9].px}px` }}>
                  <th
                    colSpan={14}
                    className="electricalSafetyReport sectionTitle noTopBorderCell noFillCell"
                  >
                    2. 부위별 측정온도
                  </th>
                </tr>

                <tr style={{ height: `${rows[10].px}px` }}>
                  <th colSpan={3} className="electricalSafetyReport subhead">
                    측정부위
                  </th>
                  <th colSpan={3} className="electricalSafetyReport subhead">
                    {point1.label}
                  </th>
                  <th colSpan={3} className="electricalSafetyReport subhead">
                    {point2.label}
                  </th>
                  <th colSpan={3} className="electricalSafetyReport subhead">
                    {point3.label}
                  </th>
                  <th colSpan={2} className="electricalSafetyReport subhead">
                    온도차
                  </th>
                </tr>

                <tr style={{ height: `${rows[11].px}px` }}>
                  <th colSpan={3} className="electricalSafetyReport label">
                    온도측정
                  </th>
                  <td
                    colSpan={3}
                    className="electricalSafetyReport value centeredValueCell"
                  >
                    {getCellValue(point1.value)}
                  </td>
                  <td
                    colSpan={3}
                    className="electricalSafetyReport value centeredValueCell"
                  >
                    {getCellValue(point2.value)}
                  </td>
                  <td
                    colSpan={3}
                    className="electricalSafetyReport value centeredValueCell"
                  >
                    {getCellValue(point3.value)}
                  </td>
                  <td
                    colSpan={2}
                    className="electricalSafetyReport value centeredValueCell"
                  >
                    {getCellValue(temperatureDiff)}
                  </td>
                </tr>

                <tr style={{ height: `${rows[12].px}px` }}>
                  <th
                    colSpan={14}
                    className="electricalSafetyReport sectionTitle noFillCell"
                  >
                    3. 측정부위의{" "}
                    <span className="electricalSafetyReport normalWeightText">
                      Thermographic
                    </span>
                  </th>
                </tr>

                <tr style={{ height: `${rows[13].px}px` }}>
                  <td
                    colSpan={7}
                    className="electricalSafetyReport imageCell centeredValueCell"
                  >
                    <ImagePlaceholder
                      src={photoSrc}
                      alt="실화상"
                      label="실화상 이미지"
                    />
                  </td>
                  <td
                    colSpan={7}
                    className="electricalSafetyReport imageCell centeredValueCell"
                  >
                    <ImagePlaceholder
                      src={thermalSrc}
                      label={t(
                        "imageFileIndex.electricalSafety.downloadModal.title",
                        {
                          defaultValue: "Triple-Spot Analysis Required",
                        },
                      )}
                      actionHint={t(
                        "imageFileIndex.electricalSafety.tripleSpotActionHint",
                        {
                          defaultValue: "Click to start triple-spot analysis",
                        },
                      )}
                      onClick={!thermalSrc ? onRequireTripleSpot : null}
                      alt="열화상"
                    />
                  </td>
                </tr>

                <tr style={{ height: `${rows[15].px}px` }}>
                  <th
                    colSpan={14}
                    className="electricalSafetyReport sectionTitle noFillCell"
                  >
                    4. 종합의견
                  </th>
                </tr>

                <tr style={{ height: `${rows[16].px}px` }}>
                  <td colSpan={14} className="electricalSafetyReport opinion">
                    {getCellValue(file?.inspection_result)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isEditMode ? (
        <ElectricalSafetyEditModal
          file={file}
          inspectorDate={inspectorDate}
          handleChange={handleChange}
          onCancel={onCancel}
          onSave={onSave}
          isSaving={isSaving}
          hasChanges={hasChanges}
        />
      ) : null}
    </>
  );
}
